import { nanoid } from "nanoid";

import { InterviewerHarnessType } from "@performyard/service-contracts";

import type { DocumentFile } from "../../../pages/Documents/schema";

import type { ChatMessage } from "../types";

import { COMPETENCY_IMPORT_INTRO } from "../constants";

export type DocumentModalState = {
	isOpen: boolean;
	document: DocumentFile | null;
};

export const initialDocumentModalState: DocumentModalState = {
	isOpen: false,
	document: null,
};

export type GreetingFlags = {
	askHrEnabled: boolean;
	goalCreationEnabled: boolean;
	formCreationEnabled: boolean;
	competencyInterviewerEnabled: boolean;
	reportBuilderEnabled: boolean;
	isOnFormBuilderPage: boolean;
	isOnGoalCreationPage: boolean;
	isOnCompetencyPage: boolean;
	goalCoachingEnabled: boolean;
	formBuildingCoachEnabled: boolean;
};

export const buildGreetingMessage = (flags: GreetingFlags): ChatMessage => {
	const capabilities: string[] = [];
	if (flags.isOnFormBuilderPage && (flags.formCreationEnabled || flags.formBuildingCoachEnabled)) {
		return {
			id: "greeting",
			role: "assistant",
			content:
				"Hi there! It looks like you're building a form. Is there anything I can assist you with for this form?",
			timestamp: new Date(),
		};
	}
	if (flags.isOnGoalCreationPage && flags.goalCoachingEnabled) {
		return {
			id: "greeting",
			role: "assistant",
			content:
				"Hi there! It looks like you're creating a goal. Is there anything I can assist you with for this goal?",
			timestamp: new Date(),
		};
	}
	if (flags.isOnCompetencyPage && flags.competencyInterviewerEnabled) {
		return {
			id: "greeting",
			role: "assistant",
			content: COMPETENCY_IMPORT_INTRO,
			showCompetencyUpload: true,
			timestamp: new Date(),
		};
	}

	if (flags.askHrEnabled) {
		capabilities.push("pull accurate, up-to-date answers from your company's internal documents");
	}
	if (flags.goalCreationEnabled) {
		capabilities.push("help you create actionable goals");
	}
	if (flags.formCreationEnabled) {
		capabilities.push("assist with creating review forms for your organization");
	}
	if (flags.competencyInterviewerEnabled) {
		capabilities.push("build a competency framework for your organization");
	}
	if (flags.reportBuilderEnabled) {
		capabilities.push("build a Reporting & Analytics report through guided questions");
	}

	let content: string;
	if (capabilities.length === 0) {
		content = "Hi there! How can I help you today?";
	} else if (capabilities.length === 1) {
		content = `Hi there! Need help? I can ${capabilities[0]}.`;
	} else {
		const last = capabilities.pop()!;
		const separator = capabilities.length > 1 ? ", or " : " or ";
		content = `Hi there! Need help? I can ${capabilities.join(", ")}${separator}${last}.`;
	}

	return {
		id: "greeting",
		role: "assistant",
		content,
		timestamp: new Date(),
	};
};

export const generateMessageId = (): string => {
	return `msg-${nanoid()}`;
};

// Session ID helpers — persisted in sessionStorage so it survives full page reloads
// (e.g. React ↔ Template page transitions) within the same tab.
const SESSION_ID_KEY = "ask-py-session-id";

export const getInterviewerNotesKey = (harness: InterviewerHarnessType): string => {
	return `ask-py-${harness}-notes`;
};
export const INTERVIEWER_HARNESS_KEY = "ask-py-interviewer-harness";

export const setActiveInterviewerHarness = (harness: InterviewerHarnessType | null): void => {
	if (harness) {
		sessionStorage.setItem(INTERVIEWER_HARNESS_KEY, harness);
	} else {
		sessionStorage.removeItem(INTERVIEWER_HARNESS_KEY);
	}
};

export const clearPersistedInterviewerState = (): void => {
	for (const harness of InterviewerHarnessType.options) {
		sessionStorage.removeItem(getInterviewerNotesKey(harness));
	}
	sessionStorage.removeItem(INTERVIEWER_HARNESS_KEY);
};

export const getOrCreateSessionId = (): string => {
	const existing = sessionStorage.getItem(SESSION_ID_KEY);
	if (existing) return existing;
	const id = nanoid();
	sessionStorage.setItem(SESSION_ID_KEY, id);
	return id;
};

export const resetSessionId = (): string => {
	const id = nanoid();
	sessionStorage.setItem(SESSION_ID_KEY, id);
	return id;
};

// Active conversation pointer (PY-13559).
//
// localStorage, not sessionStorage: a conversation is a saved thing the user
// chose, so closing the tab should return them to it rather than silently
// starting a new one. That is the whole difference from `sessionId` above, which
// stays per-tab because it identifies a tracing session, not a conversation.
const CONVERSATION_ID_KEY = "ask-py-conversation-id";

export const getStoredConversationId = (): string | null => {
	try {
		return localStorage.getItem(CONVERSATION_ID_KEY);
	} catch {
		// Private browsing modes can throw on access. No pointer just means a new chat.
		return null;
	}
};

export const setStoredConversationId = (conversationId: string): void => {
	try {
		localStorage.setItem(CONVERSATION_ID_KEY, conversationId);
	} catch {
		// Non-fatal: the conversation is still saved server-side and reachable
		// from Recent Conversations, it just won't auto-resume next visit.
	}
};

export const clearStoredConversationId = (): void => {
	try {
		localStorage.removeItem(CONVERSATION_ID_KEY);
	} catch {
		// Non-fatal, as above.
	}
};

// Durable record of what a ubiquitous (goal/form/form-question) suggestion card has
// already done, keyed by suggestion id.
//
// `sessionSuggestionStates` in conversation-slice.ts covers switching between
// conversations within one running app, but it's in-memory only — a full page reload
// or a brand-new tab loses it, and a restored card with no known outcome falls back to
// looking freshly suggested. For a goal or form suggestion that is unsafe: restored
// ubiquitous cards stay actionable (unlike coaching cards, which are never actionable
// once restored), so without this a user could recreate the same goal or form, or
// re-add the same question, just by reloading. localStorage survives that; keyed by
// suggestion id (a UUID) rather than conversation, since that's what the restored card
// itself is matched against.
export type PersistedSuggestionOutcome =
	| { kind: "goal"; createdGoalId: string }
	| { kind: "form"; createdFormId: string; createdFormBaseId: string }
	| { kind: "formQuestion" }
	// Coaching cards are never re-armed once restored, so this only keeps the
	// "Change Applied" confirmation on a card whose change really landed.
	| { kind: "responseImprovement" };

const CREATED_SUGGESTIONS_KEY = "ask-py-created-suggestions";

const readPersistedSuggestionOutcomes = (): Record<string, PersistedSuggestionOutcome> => {
	try {
		const raw = localStorage.getItem(CREATED_SUGGESTIONS_KEY);
		return raw ? (JSON.parse(raw) as Record<string, PersistedSuggestionOutcome>) : {};
	} catch {
		// Private browsing, corrupted JSON, etc. — worst case a restored card looks
		// freshly suggested again, same as before this persistence existed.
		return {};
	}
};

export const getPersistedSuggestionOutcomes = (): Record<string, PersistedSuggestionOutcome> =>
	readPersistedSuggestionOutcomes();

export const persistSuggestionOutcome = (suggestionId: string, outcome: PersistedSuggestionOutcome): void => {
	try {
		const outcomes = readPersistedSuggestionOutcomes();
		outcomes[suggestionId] = outcome;
		localStorage.setItem(CREATED_SUGGESTIONS_KEY, JSON.stringify(outcomes));
	} catch {
		// Non-fatal: worst case the card can be re-created after a reload, same as
		// before this persistence existed.
	}
};
